export interface AgenteIAConfig {
  id: number;
  nome: string;
  descricao: string;
  mensagem_inicial: string;
  instrucoes_personalizadas: string;
  mensagem_fallback: string;
  permite_atendimento_humano: number;
  imagem_id?: string;
  ativo: number;
}

export interface ConversaIA {
  id: number;
  /** Sinal recente de digitação do outro lado da conversa. */
  digitando_cliente?: boolean;
  digitando_atendente?: boolean;
  cod_organizacao?: number;
  cod_empresa?: number;
  titulo: string;
  status: string;
  necessita_humano: number;
  criado_por_login_id: number;
  responsavel_login_id?: number | null;
  ultima_mensagem_em: string | null;
  whatsapp_fixada_em?: number | string;
  favorita?: number | boolean;
  ultima_mensagem?: string;
  ultima_mensagem_id?: number;
  ultima_mensagem_autor_tipo?: MensagemIA["autor_tipo"];
  ultima_mensagem_autor_login_id?: number | null;
  agente_nome?: string;
  agente_descricao?: string;
  agente_ativo?: number;
  agente_imagem_id?: string;
  permite_atendimento_humano?: number;
  cliente_nome?: string;
  cliente_avatar_id?: string;
  canal?: "SUPORTE_CLIENTE" | "VENDAS" | "PRODUCAO_DTF" | "AJUDA_PLATAFORMA" | "DUVIDAS_CLIENTE";
  /** De onde a conversa chegou: o aplicativo ou um número de WhatsApp conectado. */
  origem?: "APLICATIVO" | "WHATSAPP";
  contato_telefone?: string;
  cliente_id?: number | null;
  /** Vendas usa o pagamento financeiro; DTF acompanha a conferência do comprovante da venda mais recente. */
  venda_status_pagamento?: "PENDENTE" | "PAGO" | "AGUARDANDO" | "EM_ANALISE" | "APROVADO" | "RECUSADO" | null;
  /** ID da mesma venda usada no pagamento, para abrir sua edição em outra aba. */
  venda_pagamento_id?: number | null;
  /** Badge de NF: pendente sem anexo na área Documentos; null quando não há venda associada. */
  venda_status_liberacao?: "PENDENTE" | "CONCLUIDO" | null;
  cliente_login_id?: number | null;
  whatsapp_contato_id?: number | null;
  cliente_responsavel_login_id?: number | null;
  cliente_responsavel_nome?: string | null;
  atendimento_por_cliente?: number;
  cliente_email?: string;
  cliente_telefone?: string;
  cliente_cpf?: string;
  cliente_cnpj?: string;
  cliente_cidade?: string;
  cliente_estado?: string;
  responsavel_nome?: string;
  responsavel_avatar_id?: string | null;
  nao_lidas?: number;
  total_mensagens?: number;
  cliente_colaborador_id?: number | null;
  vendedor_nome?: string;
  motivo_transferencia?: string;
  atribuido_em?: string;
  created_at?: string;
  tags?: TagConversaIA[];
  tags_texto?: string;
}

export interface TagConversaIA { id: number; tag: string; cor?: string | null; etiqueta_id?: number | null; emoji?: string | null }
export interface EtiquetaConversaIA { id: number; nome: string; cor: string | null; emoji: string | null; ativo: boolean; selecionada: boolean }
export interface AnexoMensagemIA { id: number; nome: string; mimeType: string; tamanho: number; url: string }
export interface ClienteConversaIA { id: number; nome: string; email?: string; telefone?: string; avatar_id?: string; cliente_login_id?: number | null; cod_colaborador?: number | null }
export interface CanalAtendimentoVendas {
  id?: number | null;
  nome: string;
  numero: string;
  status: string;
  habilitado: boolean;
  distribuicaoAutomatica?: boolean;
  atendimentoPorCliente?: boolean;
  qrCodeDataUrl?: string | null;
  ultimoErro?: string | null;
}
export interface VendedorAtendimentoIA { codLogin: number; codColaborador: number; nome: string }

export interface MensagemIA {
  id: number;
  autor_tipo: "USUARIO" | "CLIENTE" | "SUPORTE" | "IA" | "SISTEMA";
  tipo?: "MENSAGEM" | "NOTA_INTERNA";
  autor_login_id?: number | null;
  autor_nome?: string | null;
  autor_avatar_id?: string | null;
  conteudo: string;
  created_at?: string;
  editado_em?: string | null;
  excluida_em?: string | null;
  /** Decisão do servidor por conexão e autoria; o DELETE valida novamente. */
  pode_excluir?: boolean;
  /** Status do WhatsApp QR Code nas mensagens enviadas: 0 na fila (sem conexão),
   *  1 enviada, 2 entregue, 3 lida, 9 não enviada; null = sem status. */
  whatsapp_status?: number | null;
  anexos?: AnexoMensagemIA[];
}

export interface AtividadeConversaIA {
  chave: string;
  acao: string;
  descricao?: string | null;
  usuarioNome?: string | null;
  data: string;
  detalhes?: string | Record<string, unknown> | null;
}

export type TipoAtendimento = "SUPORTE" | "VENDAS" | "PRODUCAO";
export type FinalidadeAgenteIA = TipoAtendimento;
/** "PRODUCAO" atende pelo canal de Vendas de DTF. */
export type DestinoChatAreaCliente = "SUPORTE" | "VENDAS" | "PRODUCAO" | "DUVIDAS";

export interface AgenteChatAreaCliente {
  id: number;
  nome: string;
  descricao: string;
  imagem_id?: string;
  ativo: number;
}

/** Pesquisa de satisfação do atendimento, respondida dentro do próprio chat. */
export interface AvaliacaoChatAreaCliente {
  solicitada: boolean;
  respondida: boolean;
  nota: number | null;
}

export interface ConversaChatAreaCliente {
  agente: AgenteChatAreaCliente;
  conversa: ConversaIA;
  mensagens: MensagemIA[];
  avaliacao?: AvaliacaoChatAreaCliente;
}

export type AbaAtendimento = "ENTRADA" | "ESPERANDO" | "FINALIZADOS";

function queryTipoAtendimento(tipo: TipoAtendimento = "SUPORTE", aba?: AbaAtendimento) {
  const params = new URLSearchParams();
  if (tipo !== "SUPORTE") params.set("tipoAtendimento", tipo);
  if (aba) params.set("aba", aba);
  return params.size ? '?' + params.toString() : '';
}

export interface AgenteOrganizacaoAdmin {
  codOrganizacao: number;
  codEmpresa: number;
  organizacao: string;
  empresa: string;
  statusOrganizacao: string;
  finalidade: FinalidadeAgenteIA;
  agenteId: number;
  nome: string;
  descricao: string;
  mensagemInicial: string;
  instrucoesPersonalizadas: string;
  mensagemFallback: string;
  permiteAtendimentoHumano: boolean;
  ativo: boolean;
  configurado: boolean;
  imagemId: string;
  imagemUrl: string;
  pdfConhecimentoId: string;
  pdfConhecimentoUrl: string;
}

export type CanalChatAtendimento = "SUPORTE" | "VENDAS" | "PRODUCAO";
export interface ChatAtendimentoEmpresaAdmin {
  codOrganizacao: number;
  codEmpresa: number;
  organizacao: string;
  empresa: string;
  canais: Record<CanalChatAtendimento, boolean>;
  ativo: boolean;
}

export interface AgentePlataformaAdmin {
  nome: string;
  imagemId: string;
  imagemUrl: string;
  pdfConhecimentoId: string;
  pdfConhecimentoUrl: string;
  instrucoes: string;
  /** Perguntas rápidas exibidas como botões no chat da plataforma. */
  perguntas: string[];
  perguntasPadrao: string[];
  ativo: boolean;
  configurado: boolean;
}

class ErroApiIA extends Error {
  status: number;
  payload?: Record<string, unknown>;
  constructor(message: string, status: number, payload?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

async function requisitar<T>(url: string, init?: RequestInit): Promise<T> {
  const resposta = await fetch(`/api/ia${url}`, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
  });
  const payload = await resposta.json().catch(() => ({})) as Record<string, unknown>;
  if (!resposta.ok) {
    throw new ErroApiIA(
      extrairMensagemErroApi(payload, "Não foi possível concluir a operação no servidor."),
      resposta.status,
      payload
    );
  }
  return payload as T;
}

export async function obterAgenteIA() {
  return (await requisitar<{ agente: AgenteIAConfig }>("/agente")).agente;
}
export async function salvarAgenteIA(dados: Record<string, unknown>) {
  return (await requisitar<{ agente: AgenteIAConfig }>("/agente", { method: "PUT", body: JSON.stringify(dados) })).agente;
}
export async function listarAgentesOrganizacoesAdmin() {
  return (await requisitar<{ organizacoes: AgenteOrganizacaoAdmin[] }>("/admin/organizacoes")).organizacoes;
}
export async function listarCanaisChatAtendimentoAdmin() {
  return (await requisitar<{ empresas: ChatAtendimentoEmpresaAdmin[] }>("/admin/chat-atendimento")).empresas;
}
export async function salvarCanaisChatAtendimentoAdmin(empresa: ChatAtendimentoEmpresaAdmin) {
  return (await requisitar<{ empresa: ChatAtendimentoEmpresaAdmin }>(
    `/admin/chat-atendimento/${empresa.codOrganizacao}/empresas/${empresa.codEmpresa}`,
    { method: "PUT", body: JSON.stringify({ canais: empresa.canais }) },
  )).empresa;
}
export async function limparMensagensChatAtendimentoAdmin(codOrganizacao: number, codEmpresa: number) {
  return requisitar<{ mensagens: number; conversas: number; arquivos: number }>(
    `/admin/chat-atendimento/${codOrganizacao}/empresas/${codEmpresa}/mensagens`,
    { method: "DELETE" },
  );
}
export async function obterAgenteOrganizacaoAdmin(codOrganizacao: number, codEmpresa: number, finalidade: FinalidadeAgenteIA) {
  return (await requisitar<{ agente: AgenteOrganizacaoAdmin }>(`/admin/organizacoes/${codOrganizacao}/empresas/${codEmpresa}/agentes/${finalidade}`)).agente;
}
export async function salvarAgenteOrganizacaoAdmin(codOrganizacao: number, codEmpresa: number, finalidade: FinalidadeAgenteIA, dados: Record<string, unknown>) {
  return (await requisitar<{ agente: AgenteOrganizacaoAdmin }>(`/admin/organizacoes/${codOrganizacao}/empresas/${codEmpresa}/agentes/${finalidade}`, {
    method: "PUT",
    body: JSON.stringify(dados),
  })).agente;
}
export async function atualizarStatusAgenteOrganizacaoAdmin(codOrganizacao: number, codEmpresa: number, finalidade: FinalidadeAgenteIA, ativo: boolean) {
  return (await requisitar<{ agente: AgenteOrganizacaoAdmin }>(`/admin/organizacoes/${codOrganizacao}/empresas/${codEmpresa}/agentes/${finalidade}/status`, {
    method: "PATCH",
    body: JSON.stringify({ ativo }),
  })).agente;
}
export interface PaginaConversasIA { conversas: ConversaIA[]; proximoCursor: string | null; contagens?: Record<AbaAtendimento, number> | null; revisaoClientes?: string }
export interface MetricasAtendimentoIA {
  atual: { slaPercentual: number; tempoRespostaHoras: number; satisfacao: number };
  anterior: { slaPercentual: number; tempoRespostaHoras: number; satisfacao: number };
}

export interface AvaliacaoAtendimentoIA {
  id: number;
  conversaId: number;
  nota: number;
  comentario: string | null;
  solicitadaEm: string;
  respondidaEm: string;
  titulo: string | null;
  /** Origem da conversa (WHATSAPP, CHAT, EMAIL, TELEFONE...) usada na coluna Canal. */
  origem?: string | null;
  clienteNome: string;
  atendenteNome: string;
}

export interface ResumoAvaliacoesAtendimentoIA {
  total: number;
  media: number;
  excelentes: number;
  criticas: number;
  porNota: Record<number, number>;
}

export async function listarConversasIA(minhas = false, tipo: TipoAtendimento = "SUPORTE", cursor?: string | null, aba: AbaAtendimento = "ENTRADA", pesquisa: { busca?: string; campoBusca?: string } = {}) {
  const params = new URLSearchParams();
  if (minhas) params.set("minhas", "1");
  if (tipo !== "SUPORTE") params.set("tipoAtendimento", tipo);
  if (cursor) params.set("cursor", cursor);
  params.set("aba", aba);
  if (pesquisa.busca?.trim()) {
    params.set("busca", pesquisa.busca.trim());
    params.set("campoBusca", pesquisa.campoBusca ?? "cliente_nome");
  }
  const query = params.toString();
  return requisitar<PaginaConversasIA>(`/conversas${query ? `?${query}` : ""}`);
}
/** Consulta o mesmo canal da tela de atendimento, com filtro opcional de WhatsApp. */
export async function obterMetricasAtendimentoIA(tipo: TipoAtendimento, origem?: "WHATSAPP") {
  const params = new URLSearchParams(queryTipoAtendimento(tipo));
  if (origem) params.set("origem", origem);
  return requisitar<MetricasAtendimentoIA>(`/atendimento/metricas${params.size ? `?${params.toString()}` : ""}`);
}
export async function listarAvaliacoesAtendimentoIA(tipo: TipoAtendimento) {
  return requisitar<{ avaliacoes: AvaliacaoAtendimentoIA[]; resumo: ResumoAvaliacoesAtendimentoIA }>(
    `/avaliacoes-atendimento${queryTipoAtendimento(tipo)}`
  );
}
export async function criarConversaIA(dados: { titulo?: string; produtoId?: number | null } = {}) {
  return requisitar<{ id: number }>("/conversas", { method: "POST", body: JSON.stringify(dados) });
}
export async function obterConversaIA(id: number, tipo: TipoAtendimento = "SUPORTE", aba: AbaAtendimento = "ENTRADA") {
  return requisitar<{ conversa: ConversaIA; mensagens: MensagemIA[] }>(`/conversas/${id}${queryTipoAtendimento(tipo, aba)}`);
}
export async function marcarConversaLidaIA(id: number, ultimaMensagemId: number, tipo: TipoAtendimento, aba: AbaAtendimento) {
  return requisitar<{ sucesso: boolean; nao_lidas: number }>(`/conversas/${id}/leitura${queryTipoAtendimento(tipo, aba)}`, {
    method: "POST", body: JSON.stringify({ ultimaMensagemId }),
  });
}
export async function listarAtividadesConversaIA(id: number, tipo: TipoAtendimento, aba: AbaAtendimento) {
  return (await requisitar<{ atividades: AtividadeConversaIA[] }>(`/conversas/${id}/atividades${queryTipoAtendimento(tipo, aba)}`)).atividades;
}
export async function baixarConversaPdfIA(id: number, tipo: TipoAtendimento, aba: AbaAtendimento) {
  const resposta = await fetch(`/api/ia/conversas/${id}/pdf${queryTipoAtendimento(tipo, aba)}`, {
    credentials: "include", headers: { Accept: "application/pdf" },
  });
  if (!resposta.ok) {
    const payload = await resposta.json().catch(() => ({})) as Record<string, unknown>;
    throw new ErroApiIA(extrairMensagemErroApi(payload, "Não foi possível gerar o PDF da conversa."), resposta.status, payload);
  }
  return resposta.blob();
}
export async function perguntarIA(id: number, mensagem: string) {
  return requisitar<{ mensagem: MensagemIA; necessitaHumano?: boolean }>(`/conversas/${id}/perguntar`, { method: "POST", body: JSON.stringify({ mensagem }) });
}
export async function abrirConversaPlataformaIA() {
  return requisitar<{ agente: AgenteIAConfig; perguntas?: string[]; conversa: ConversaIA; mensagens: MensagemIA[] }>(
    "/plataforma/conversa",
    { method: "POST", body: "{}" }
  );
}
/** Agente que responde dúvidas sobre o uso da plataforma (configurado pelo dono da plataforma). */
export async function obterAgentePlataformaAdmin() {
  return (await requisitar<{ agente: AgentePlataformaAdmin }>("/plataforma/agente")).agente;
}
export async function salvarAgentePlataformaAdmin(dados: Record<string, unknown>) {
  return (await requisitar<{ agente: AgentePlataformaAdmin }>("/plataforma/agente", {
    method: "PUT",
    body: JSON.stringify(dados),
  })).agente;
}
export async function atualizarStatusAgentePlataformaAdmin(ativo: boolean) {
  return (await requisitar<{ agente: AgentePlataformaAdmin }>("/plataforma/agente/status", {
    method: "PATCH",
    body: JSON.stringify({ ativo }),
  })).agente;
}
export async function perguntarPlataformaIA(id: number, mensagem: string) {
  return requisitar<{ mensagem: MensagemIA }>(`/plataforma/conversas/${id}/perguntar`, {
    method: "POST",
    body: JSON.stringify({ mensagem }),
  });
}
/**
 * Nome e foto do agente para o cabeçalho, antes de existir conversa, junto dos
 * destinos que a empresa consegue atender (quem não tem atendente cadastrado
 * não aparece na escolha).
 */
export async function obterAgenteAreaCliente(destino?: DestinoChatAreaCliente) {
  const consulta = destino ? `?destino=${destino}` : "";
  const resposta = await requisitar<{
    agente: AgenteChatAreaCliente;
    destinos?: DestinoChatAreaCliente[];
  }>(`/area-cliente/agente${consulta}`);
  return { agente: resposta.agente, destinos: resposta.destinos ?? ["DUVIDAS"] };
}
export async function abrirConversaAreaCliente(destino: DestinoChatAreaCliente) {
  return requisitar<ConversaChatAreaCliente>("/area-cliente/conversa", {
    method: "POST",
    body: JSON.stringify({ destino }),
  });
}
/** Avisa que o cliente está escrevendo (o atendente vê os pontinhos). */
export async function sinalizarDigitandoAreaCliente(id: number, destino: DestinoChatAreaCliente) {
  return requisitar<{ sucesso: boolean }>(`/area-cliente/conversas/${id}/digitando?destino=${destino}`, {
    method: "POST",
  });
}
/** Avisa que o atendente está escrevendo (o cliente vê os pontinhos). */
export async function sinalizarDigitandoConversaIA(id: number, tipo: TipoAtendimento, aba: AbaAtendimento) {
  return requisitar<{ sucesso: boolean }>(`/conversas/${id}/digitando${queryTipoAtendimento(tipo, aba)}`, {
    method: "POST",
  });
}
export async function obterConversaAreaCliente(id: number, destino: DestinoChatAreaCliente) {
  return requisitar<ConversaChatAreaCliente>(`/area-cliente/conversas/${id}?destino=${destino}`);
}
export async function encerrarConversaAreaCliente(id: number, destino: DestinoChatAreaCliente) {
  return requisitar<{ sucesso: boolean }>(`/area-cliente/conversas/${id}/encerrar`, {
    method: "POST",
    body: JSON.stringify({ destino }),
  });
}

export async function responderAvaliacaoAreaCliente(
  id: number,
  destino: DestinoChatAreaCliente,
  nota: number,
  comentario?: string,
) {
  return requisitar<ConversaChatAreaCliente>(`/area-cliente/conversas/${id}/avaliacao`, {
    method: "POST",
    body: JSON.stringify({ destino, nota, comentario }),
  });
}

export async function enviarMensagemAreaCliente(
  id: number,
  destino: DestinoChatAreaCliente,
  dados: { mensagem?: string; base64?: string; nomeArquivo?: string },
) {
  const caminho = destino === "DUVIDAS" ? "mensagens-ia" : "mensagens";
  return requisitar<{
    mensagem: MensagemIA;
    resposta?: MensagemIA;
    agente?: AgenteChatAreaCliente;
  }>(`/area-cliente/conversas/${id}/${caminho}`, {
    method: "POST",
    body: JSON.stringify({ ...dados, destino }),
  });
}
export async function responderSuporteIA(id: number, mensagem: string, tipo: TipoAtendimento = "SUPORTE", aba: AbaAtendimento = "ESPERANDO") {
  return requisitar<{ mensagem: MensagemIA }>(`/conversas/${id}/responder-suporte${queryTipoAtendimento(tipo, aba)}`, { method: "POST", body: JSON.stringify({ mensagem }) });
}
export async function editarMensagemSuporteIA(id: number, mensagemId: number, mensagem: string, tipo: TipoAtendimento = "SUPORTE", aba: AbaAtendimento = "ESPERANDO") {
  return requisitar<{ mensagem: MensagemIA }>(`/conversas/${id}/mensagens/${mensagemId}${queryTipoAtendimento(tipo, aba)}`, { method: "PUT", body: JSON.stringify({ mensagem }) });
}
export async function criarNotaInternaIA(id: number, conteudo: string, tipo: TipoAtendimento = "SUPORTE", aba: AbaAtendimento = "ESPERANDO") {
  return requisitar<{ nota: MensagemIA }>(`/conversas/${id}/notas${queryTipoAtendimento(tipo, aba)}`, {
    method: "POST", body: JSON.stringify({ conteudo }),
  });
}
export async function excluirNotaInternaIA(id: number, notaId: number, tipo: TipoAtendimento = "SUPORTE", aba: AbaAtendimento = "ESPERANDO") {
  return requisitar<{ sucesso: boolean }>(`/conversas/${id}/notas/${notaId}${queryTipoAtendimento(tipo, aba)}`, { method: "DELETE" });
}
export async function excluirMensagemSuporteIA(id: number, mensagemId: number, tipo: TipoAtendimento = "SUPORTE", aba: AbaAtendimento = "ESPERANDO") {
  return requisitar<{ sucesso: boolean }>(`/conversas/${id}/mensagens/${mensagemId}${queryTipoAtendimento(tipo, aba)}`, { method: "DELETE" });
}
export async function obterDisponibilidadeSuporte() {
  return requisitar<{ status: "DISPONIVEL" | "AUSENTE" | "OCUPADO" | "OFFLINE" }>("/suporte/disponibilidade");
}
export async function atualizarDisponibilidadeSuporte(status: "DISPONIVEL" | "AUSENTE" | "OCUPADO" | "OFFLINE") {
  return requisitar<{ status: string; atribuicao?: { atribuido: boolean } | null }>("/suporte/disponibilidade", { method: "PUT", body: JSON.stringify({ status }) });
}
export async function atenderConversaIA(id: number, tipo: TipoAtendimento = "SUPORTE", aba: AbaAtendimento = "ESPERANDO") {
  return requisitar(`/conversas/${id}/atender${queryTipoAtendimento(tipo, aba)}`, { method: "POST", body: "{}" });
}
export async function marcarMeuClienteIA(id: number, meuCliente: boolean, tipo: TipoAtendimento, aba: AbaAtendimento) {
  return requisitar<{ clienteResponsavelLoginId: number | null }>(`/conversas/${id}/meu-cliente${queryTipoAtendimento(tipo, aba)}`, {
    method: "PUT", body: JSON.stringify({ meuCliente }),
  });
}
export type DestinatarioConversaIA = { codLogin: number; nome: string; status: string | null };
export async function listarDestinatariosConversaIA(id: number, tipo: TipoAtendimento, aba: AbaAtendimento) {
  return (await requisitar<{ atendentes: DestinatarioConversaIA[] }>(`/conversas/${id}/atendentes${queryTipoAtendimento(tipo, aba)}`)).atendentes;
}
export async function direcionarConversaIA(id: number, responsavelLoginId: number, tipo: TipoAtendimento, aba: AbaAtendimento) {
  return requisitar(`/conversas/${id}/direcionar${queryTipoAtendimento(tipo, aba)}`, {
    method: "POST", body: JSON.stringify({ responsavelLoginId }),
  });
}
export async function alternarFavoritoConversaIA(id: number, favorito: boolean, tipo: TipoAtendimento, aba: AbaAtendimento) {
  return requisitar<{ sucesso: boolean; favorito: boolean; fixadaEm: number }>(`/conversas/${id}/favorito${queryTipoAtendimento(tipo, aba)}`, {
    method: "PUT", body: JSON.stringify({ favorito }),
  });
}
/** Remove todos os favoritos do login atual no canal da tela (Tickets, Vendas ou Vendas de DTF). */
export async function limparFavoritosConversasIA(tipo: TipoAtendimento, aba: AbaAtendimento) {
  return requisitar<{ sucesso: boolean; removidos: number }>(`/conversas/favoritos${queryTipoAtendimento(tipo, aba)}`, {
    method: "DELETE",
  });
}
export async function transferirConversaParaAgenteIA(id: number, tipo: TipoAtendimento = "SUPORTE") {
  return requisitar<{ status: "ABERTA" }>(`/conversas/${id}/transferir-agente${queryTipoAtendimento(tipo, "ESPERANDO")}`, { method: "POST", body: "{}" });
}
export async function listarClientesConversaIA(busca = "", tipo: TipoAtendimento = "SUPORTE") {
  const params = new URLSearchParams({ q: busca });
  if (tipo !== "SUPORTE") params.set("tipoAtendimento", tipo);
  return (await requisitar<{ clientes: ClienteConversaIA[] }>(`/suporte/clientes?${params.toString()}`)).clientes;
}
export async function obterCanalAtendimentoVendas() {
  return (await requisitar<{ canal: CanalAtendimentoVendas }>("/vendas/canal")).canal;
}
export async function obterCanalAtendimentoIA(tipo: TipoAtendimento = "SUPORTE") {
  return (await requisitar<{ canal: CanalAtendimentoVendas }>(`/atendimento/canal${queryTipoAtendimento(tipo)}`)).canal;
}
export async function reconectarCanalAtendimentoIA(id: number | null | undefined, tipo: TipoAtendimento = "SUPORTE") {
  const caminho = id ? `/atendimento/canal/${id}/reconectar` : "/atendimento/canal/reconectar";
  return (await requisitar<{ canal: CanalAtendimentoVendas }>(`${caminho}${queryTipoAtendimento(tipo)}`, { method: "POST", body: "{}" })).canal;
}
/** Religa o canal com o pareamento existente — não gera um novo QR Code. */
export async function religarCanalAtendimentoIA(id: number | null | undefined, tipo: TipoAtendimento = "SUPORTE") {
  const caminho = id ? `/atendimento/canal/${id}/religar` : "/atendimento/canal/religar";
  return (await requisitar<{ canal: CanalAtendimentoVendas }>(`${caminho}${queryTipoAtendimento(tipo)}`, { method: "POST", body: "{}" })).canal;
}
export async function obterQrCanalAtendimentoIA(id: number, tipo: TipoAtendimento = "SUPORTE") {
  return (await requisitar<{ canal: CanalAtendimentoVendas }>(`/atendimento/canal/${id}/qr${queryTipoAtendimento(tipo)}`)).canal;
}
export async function listarVendedoresAtendimentoIA() {
  return (await requisitar<{ vendedores: VendedorAtendimentoIA[] }>("/vendas/vendedores")).vendedores;
}
export async function atribuirVendedorConversaIA(id: number, codColaborador: number | null) {
  return requisitar<{ vendedor: VendedorAtendimentoIA | null }>(`/conversas/${id}/vendedor?tipoAtendimento=VENDAS&aba=ESPERANDO`, {
    method: "PUT",
    body: JSON.stringify({ codColaborador }),
  });
}
export async function criarConversaSuporteIA(clienteId: number, mensagem = "", tipo: TipoAtendimento = "SUPORTE") {
  return requisitar<{ id: number }>("/suporte/conversas", { method: "POST", body: JSON.stringify({ clienteId, mensagem, tipoAtendimento: tipo }) });
}
export async function criarContatoWhatsappAtendimentoIA(dados: { nome: string; celular: string; mensagem: string }, tipo: TipoAtendimento) {
  return requisitar<{ id: number }>(`/suporte/contatos${queryTipoAtendimento(tipo)}`, {
    method: "POST", body: JSON.stringify(dados),
  });
}
export async function salvarContatoAparelhoConversaIA(id: number, nome: string, tipo: TipoAtendimento, aba: AbaAtendimento) {
  return requisitar<{ nome: string; whatsappContatoId: number }>(`/conversas/${id}/contato-aparelho${queryTipoAtendimento(tipo, aba)}`, {
    method: "PUT", body: JSON.stringify({ nome }),
  });
}
export async function adicionarTagConversaIA(id: number, tag: string, cor?: string, tipo: TipoAtendimento = "SUPORTE") {
  return requisitar(`/conversas/${id}/tags${queryTipoAtendimento(tipo, "ESPERANDO")}`, { method: "POST", body: JSON.stringify({ tag, cor }) });
}
export async function obterEtiquetasConversaIA(id: number, tipo: TipoAtendimento, aba: AbaAtendimento) {
  return (await requisitar<{ etiquetas: EtiquetaConversaIA[] }>(`/conversas/${id}/etiquetas${queryTipoAtendimento(tipo, aba)}`)).etiquetas;
}
export async function salvarEtiquetasConversaIA(id: number, etiquetaIds: number[], tipo: TipoAtendimento) {
  return (await requisitar<{ etiquetas: EtiquetaConversaIA[] }>(`/conversas/${id}/etiquetas${queryTipoAtendimento(tipo, "ESPERANDO")}`, {
    method: "PUT", body: JSON.stringify({ etiquetaIds }),
  })).etiquetas;
}
export async function atualizarCorTagConversaIA(id: number, tagId: number, cor: string, tipo: TipoAtendimento = "SUPORTE") {
  return requisitar<{ cor: string }>(`/conversas/${id}/tags/${tagId}${queryTipoAtendimento(tipo, "ESPERANDO")}`, { method: "PUT", body: JSON.stringify({ cor }) });
}
export async function removerTagConversaIA(id: number, tagId: number, tipo: TipoAtendimento = "SUPORTE") {
  return requisitar(`/conversas/${id}/tags/${tagId}${queryTipoAtendimento(tipo, "ESPERANDO")}`, { method: "DELETE" });
}
export async function anexarDocumentoConversaIA(id: number, dados: { base64: string; nomeArquivo: string; mensagem?: string }, tipo: TipoAtendimento = "SUPORTE", aba: AbaAtendimento = "ESPERANDO") {
  return requisitar(`/conversas/${id}/anexos${queryTipoAtendimento(tipo, aba)}`, { method: "POST", body: JSON.stringify(dados) });
}
export async function solicitarAtendimentoHumanoIA(id: number) {
  return requisitar(`/conversas/${id}/solicitar-humano`, { method: "POST", body: "{}" });
}
export async function encerrarConversaIA(id: number, tipo: TipoAtendimento = "SUPORTE") {
  return requisitar(`/conversas/${id}/encerrar${queryTipoAtendimento(tipo, "ESPERANDO")}`, { method: "POST", body: "{}" });
}

export { ErroApiIA };
import { extrairMensagemErroApi } from "../utils/mensagemErroApi";
